import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileBottomBar from '../../components/feature/MobileBottomBar';
import QuoteForm from '../../components/base/QuoteForm';
import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';

const OfficeRemovalsPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'Office Removals Lancashire | Business Relocation | Dan & Van',
    description: 'Seamless office relocations with minimal downtime. Weekend & evening moves available. IT equipment care, fully insured. Lancashire business moves — 07530 166035',
    keywords: 'office removals Lancashire, business relocation, commercial moves, office movers, IT equipment moving, weekend office moves',
    canonical: '/office-removals',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Office Removals',
      provider: {
        '@type': 'MovingCompany',
        name: 'Dan & Van Removals',
        telephone: '07530166035',
        areaServed: 'Lancashire, UK',
      },
      description: 'Professional office relocations across Lancashire with minimal disruption to your business. Specialist IT equipment handling, weekend and out-of-hours moves available.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceRange: '££',
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: 'ri-time-line',
      title: 'Minimal Downtime',
      description: 'Weekend and out-of-hours moves to keep your business running',
    },
    {
      icon: 'ri-computer-line',
      title: 'IT Equipment Care',
      description: 'Specialist handling of computers, servers, and electronics',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Fully Insured',
      description: 'Comprehensive commercial insurance coverage',
    },
    {
      icon: 'ri-team-line',
      title: 'Experienced Team',
      description: 'Professional crew trained in office relocations',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <MobileBottomBar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden mt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/996e29e2193e2e0aabbfee45d4363b91.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-gold text-xs uppercase tracking-wider mb-4"
          >
            <Link to="/" className="hover:underline cursor-pointer">Home</Link> / Services / Office Removals
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-sans text-6xl font-bold mb-6"
          >
            Office Removals
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-white/90 mb-8"
          >
            Seamless business relocations with minimal disruption
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link
              to="/contact"
              className="inline-block px-10 py-4 bg-gold text-charcoal font-semibold text-lg rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="font-sans text-4xl font-bold text-charcoal mb-6">
                Professional office relocations across Lancashire
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Moving your business doesn't have to mean days of downtime. At Dan & Van Removals, we specialise in efficient office relocations that minimise disruption to your operations. Our experienced team understands the unique challenges of commercial moves and works around your schedule to ensure a smooth transition.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                From small startups to established businesses, we handle office moves of all sizes across Lancashire. We'll work with you to create a detailed moving plan that keeps your business running smoothly.
              </p>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6 mt-12">
                What's included in our office removals service
              </h3>

              <ul className="space-y-4 mb-12">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Pre-move planning:</strong> Detailed site survey and customised moving schedule
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">IT equipment handling:</strong> Specialist care for computers, servers, and electronics
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Furniture disassembly and reassembly:</strong> Desks, partitions, and storage units
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Secure document handling:</strong> Confidential files transported safely
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Weekend and evening moves:</strong> Relocate outside business hours to avoid downtime
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Full commercial insurance:</strong> Comprehensive cover for your business assets
                  </span>
                </li>
              </ul>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6">
                Minimising business disruption
              </h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We understand that time is money in business. That's why we offer flexible moving schedules, including weekend and out-of-hours relocations. Our team works efficiently to have your office up and running in your new location as quickly as possible.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                We'll label and track all items, ensuring everything arrives at the right location in your new office. Our systematic approach means you can be operational again with minimal delay.
              </p>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6">
                Specialist IT and equipment handling
              </h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Your IT infrastructure is the backbone of your business. Our team is trained in the careful handling and transportation of computers, servers, printers, and other electronic equipment. We use anti-static packaging and secure transportation methods to protect your valuable technology.
              </p>

              <div className="bg-gray-50 border-l-4 border-gold p-6 rounded">
                <p className="text-lg text-charcoal font-medium mb-2">
                  Planning an office move?
                </p>
                <p className="text-gray-700 mb-4">
                  Contact us for a free consultation and detailed quote. We'll work with you to create a moving plan that suits your business needs.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:07530166035"
                    className="px-6 py-3 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    Call 07530 166035
                  </a>
                  <Link
                    to="/contact"
                    className="px-6 py-3 bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    Get a Quote
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-white border border-gold/20 rounded-lg p-8 shadow-luxury mb-8">
                  <h3 className="font-sans text-2xl font-bold text-charcoal mb-6">
                    Get Your Free Quote
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Contact us directly for a fast, free quote on your office relocation.
                  </p>
                  <div className="space-y-3">
                    <a
                      href="https://wa.me/447530166035"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full py-3 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line mr-2 text-xl"></i>
                      WhatsApp Us
                    </a>
                    <a
                      href="tel:07530166035"
                      className="flex items-center justify-center w-full py-3 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2 text-lg"></i>
                      Call Now
                    </a>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  {features.map((feature, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 p-4 rounded-lg flex items-start"
                    >
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mr-3">
                        <i className={`${feature.icon} text-2xl text-gold`}></i>
                      </div>
                      <div>
                        <h4 className="font-sans font-bold text-charcoal mb-1">
                          {feature.title}
                        </h4>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h3 className="font-sans text-3xl font-bold text-charcoal mb-8 text-center">
            Other services you might need
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/house-removals"
              className="group relative h-64 rounded-lg overflow-hidden cursor-pointer"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=professional%20movers%20loading%20wrapped%20furniture%20into%20white%20removal%20van%20outside%20modern%20home%20protective%20equipment%20visible%20premium%20moving%20service&width=800&height=600&seq=house003&orientation=landscape')`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h4 className="font-sans text-2xl font-bold mb-2">House Removals</h4>
                <span className="text-gold flex items-center">
                  Learn more
                  <i className="ri-arrow-right-line ml-2"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/single-item-moves"
              className="group relative h-64 rounded-lg overflow-hidden cursor-pointer"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=single%20large%20furniture%20piece%20being%20carefully%20moved%20by%20professional%20movers%20with%20protective%20wrapping%20simple%20clean%20background%20premium%20service&width=800&height=600&seq=single003&orientation=landscape')`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h4 className="font-sans text-2xl font-bold mb-2">Single Item Moves</h4>
                <span className="text-gold flex items-center">
                  Learn more
                  <i className="ri-arrow-right-line ml-2"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/collections-deliveries"
              className="group relative h-64 rounded-lg overflow-hidden cursor-pointer"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=delivery%20van%20with%20parcels%20and%20boxes%20professional%20courier%20service%20clean%20modern%20vehicle%20simple%20background%20efficient%20logistics&width=800&height=600&seq=delivery003&orientation=landscape')`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h4 className="font-sans text-2xl font-bold mb-2">Collections & Deliveries</h4>
                <span className="text-gold flex items-center">
                  Learn more
                  <i className="ri-arrow-right-line ml-2"></i>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OfficeRemovalsPage;
